import { useEffect, useState } from 'react';
import { type UserInfo } from '@monorepo-hol/providers';

import type { PdfData } from '../../../types/documents';
import {
  type LangStateType,
  useLanguageContext,
} from '../../../provider/LanguageProvider';
import {
  appendiceType,
  langType,
  useGetAppendicesDocument2,
} from '../../../modules/document';
import type { DataDocumentDetail } from '../services/document-detail/types';
import type { AccessRight } from '../../../types/metadata';
import type { AppendiceType } from '../types';
import { MainCatalog } from '../../../types/catalog';

export interface UseAppendicesDataProps {
  guid: string;
  docId?: string;
  user: UserInfo;
  data: DataDocumentDetail;
  type: AppendiceType;
  catalogAcl: AccessRight;
  catalogData?: MainCatalog;
}

export interface IUseAppendicesData {
  isDocumentReady: boolean;
  isDocumentError: boolean;
  pdfDoc: PdfData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdfDocList: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lampiranList?: any[];
  LangState: LangStateType;

  isAppendiceDataLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appendiceDocument?: any;
}

export function useAppendicesData({
  guid,
  docId,
  data,
  user,
  type,
  catalogAcl,
  catalogData,
}: UseAppendicesDataProps): IUseAppendicesData {
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<PdfData>(null);
  const [isDocumentError, setIsDocumentError] = useState(false);
  const [pdfDocList, setPdfDocList] = useState([]);
  const { LangState } = useLanguageContext();

  const documentList = data?.file?.main?.filter(
    (catalogFile) =>
      catalogFile.mimetype === 'application/pdf' && catalogFile.language == 'id'
  );

  const consolidationDocumentList = data?.file?.consolidations?.filter(
    (catalogFile) => catalogFile.mimetype === 'application/pdf'
  );

  const apendiceType: appendiceType = {
    main: 'main',
    consolidations: 'consolidation',
    translations: 'main',
  }[type] as appendiceType;

  const lang: langType = {
    main: 'id',
    consolidations: 'id',
    translations: 'en',
  }[type] as langType;

  const authorized = {
    main: data?.catalog_access_right === 'authorized',
    consolidations: catalogAcl?.consolidations === 'authorized',
    translations: catalogAcl?.translations === 'authorized',
  }[type];

  const {
    isAppendiceDataLoading,
    appendiceData,
    appendiceDataStatus,
    documentMetadataStatus,
  } = useGetAppendicesDocument2({
    guid: guid,
    jwt: user?.jwt || '',
    type: 'appendices',
    docId: catalogData?.pdfDocumentGuid,
    mainDocument: catalogData?.mainDocument,
    apendiceType,
    lang,
    authorized,
  });

  useEffect(() => {
    setPdfDocList(
      type === 'consolidations' ? consolidationDocumentList : documentList
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (appendiceDataStatus === 'success') {
      setIsDocumentReady(true);
      setPdfDoc(appendiceData);
    } else if (appendiceDataStatus === 'error') {
      setIsDocumentReady(true);
      setPdfDoc(appendiceData);
      setIsDocumentError(true);
    } else if (
      documentMetadataStatus === 'success' &&
      appendiceDataStatus === 'idle'
    ) {
      setIsDocumentReady(true);
      setPdfDoc(appendiceData);
    } else if (appendiceDataStatus === 'loading') {
      setIsDocumentReady(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendiceDataStatus, documentMetadataStatus]);

  return {
    isDocumentReady,
    pdfDoc,
    pdfDocList,
    LangState,
    isAppendiceDataLoading,
    isDocumentError,
  };
}
